/**
 * Square power — how valuable each square is on its own, independent of what
 * stands on it. A child can learn that "the middle is where the fighting
 * happens" before they can read a legal-move list.
 *
 * Deliberately **not** a function of the position. `controlScores` answers
 * "who controls this square right now"; this answers "which squares are worth
 * taking". That is why it survives an empty board and why the overlay looks
 * identical in every position — see the naming caveat on `HEATMAP_MODES`.
 *
 * Variant-agnostic by construction: it only ever reads the coordinate, so
 * checkers gets it verbatim. Rank 8 scoring 0 is correct for chess and is a
 * trap for checkers, where rank 8 is the promotion row — the checkers copy
 * adds its own marker for that rather than changing this function, so there is
 * no second power map that can drift from this one.
 */

/** The four power tiers, ascending. Index is the Chebyshev ring index. */
export const SQUARE_POWER = [0, 2, 4, 6] as const;

export type SquarePower = (typeof SQUARE_POWER)[number];

/**
 * Ring index from the centre 2×2 (d4/e4/d5/e5): `max(dx, dy)`, where each is
 * the distance to the *nearest* centre file or rank. Using the nearest rather
 * than a fixed origin is what makes the map symmetric under 180° rotation.
 *
 * `file` is 0-indexed from the a-file, so d/e are 3/4. `rank` is 0-indexed from
 * rank 1, so ranks 4/5 are **3/4** — not 4/5. Off-by-one here shifts the whole
 * map one rank down, which still passes every per-square spot check someone
 * would write by eye and only shows up in the symmetry tests.
 */
function ringOf(file: number, rank: number): number {
  const dx = Math.min(Math.abs(file - 3), Math.abs(file - 4));
  const dy = Math.min(Math.abs(rank - 3), Math.abs(rank - 4));
  return Math.max(dx, dy);
}

/**
 * Power of a square, e.g. `powerOf('e4') === 6`, `powerOf('a1') === 0`.
 *
 * A square that is not a legal coordinate throws rather than guessing. A
 * silently-defaulted power function would paint the whole board one colour for
 * a typo, and "every square is worth 6" is exactly the wrong thing to teach.
 */
export function powerOf(square: string): SquarePower {
  if (square.length !== 2) {
    throw new RangeError(`powerOf: not a square: ${JSON.stringify(square)}`);
  }
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]) - 1;
  if (file < 0 || file > 7 || Number.isNaN(rank) || rank < 0 || rank > 7) {
    throw new RangeError(`powerOf: not a square: ${JSON.stringify(square)}`);
  }
  const ring = ringOf(file, rank);
  // Ring 0 is the centre 2×2 (strongest), ring 3+ is the outer rim (0).
  return ring < SQUARE_POWER.length - 1
    ? SQUARE_POWER[SQUARE_POWER.length - 1 - ring]!
    : 0;
}

/** Every square in board order (a8 → h1), for rendering and for tests. */
export function allSquares(): string[] {
  const out: string[] = [];
  for (let rank = 8; rank >= 1; rank -= 1) {
    for (let file = 0; file < 8; file += 1) {
      out.push(`${String.fromCharCode(97 + file)}${rank}`);
    }
  }
  return out;
}

/** square → power, for callers that need the whole board in one pass. */
export function powerMap(): Record<string, SquarePower> {
  const out: Record<string, SquarePower> = {};
  for (const sq of allSquares()) out[sq] = powerOf(sq);
  return out;
}
